import { useState } from 'react';
import { Zap, Loader, Copy, Check, RefreshCw } from 'lucide-react';
import { Card, SectionLabel, Input, Select, Btn } from '../components/UI';

const SERVICES = [
  'Thread writing (5 threads, $50 USDT)',
  'Full growth plan (7-day, $100 USDT)',
  'KOL management & outreach',
  'Community moderation (trial)',
  'Social media management',
  'Content calendar creation',
];

export default function AIPitchPage() {
  const [form, setForm] = useState({
    projectName: '',
    projectDesc: '',
    service: SERVICES[0],
    tone: 'direct',
    platform: 'X DM',
  });
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!form.projectName.trim()) { setError('Enter a project name first.'); return; }
    setError('');
    setLoading(true);
    setResult('');
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
  'Content-Type': 'application/json',
  'x-api-key': process.env.REACT_APP_ANTHROPIC_API_KEY,
  'anthropic-version': '2023-06-01',
  'anthropic-dangerous-direct-browser-access': 'true',
},
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system: `You are Ade, a Web3 growth marketer from Nigeria with deep experience in KOL management, social media, community growth, and content writing for crypto projects. You write sharp, direct, non-cringe cold DM pitches that actually get replies. Your pitches are personalized, specific, short (under 120 words), and end with a simple yes/no question. Never use buzzwords like "synergies" or "leverage". Sound like a real person, not a bot. Return ONLY the pitch text — no labels, no explanation, no quotes around it.`,
          messages: [{
            role: 'user',
            content: `Write a cold DM pitch for me to send on ${form.platform}.

Project I'm pitching: ${form.projectName}
${form.projectDesc ? `What I know about them: ${form.projectDesc}` : ''}
Service I'm offering: ${form.service}
Tone: ${form.tone}

Write a short, personalized pitch that sounds like a real human wrote it. Under 120 words. End with a clear yes/no question.`,
          }],
        }),
      });
      const data = await res.json();console.log('API response:', JSON.stringify(data));
      const text = data.content?.find(b => b.type === 'text')?.text || '';
      if (!text) throw new Error('No response from AI');
      setResult(text);
    } catch (e) {
  setError(JSON.stringify(e.message));
}
    setLoading(false);
  };

  const copy = () => {
    navigator.clipboard.writeText(result).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em' }}>AI Pitch Writer</h1>
          <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', background: 'var(--accent-dim)', color: 'var(--accent)', padding: '3px 9px', borderRadius: 20 }}>Powered by Claude</span>
        </div>
        <p style={{ color: 'var(--text2)', fontSize: 14 }}>Tell it about the project. Get a personalized pitch in seconds.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <div>
          <SectionLabel>Project details</SectionLabel>
          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6, fontFamily: 'var(--font-mono)' }}>Project name *</label>
                <Input
                  placeholder="e.g. SynthFi, ApeDAO, MintedX"
                  value={form.projectName}
                  onChange={e => set('projectName', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6, fontFamily: 'var(--font-mono)' }}>What you know about them (optional)</label>
                <textarea
                  placeholder="e.g. Pre-TGE DeFi protocol, launching next month, active on X, 2k followers..."
                  value={form.projectDesc}
                  onChange={e => set('projectDesc', e.target.value)}
                  style={{
                    width: '100%', background: 'var(--bg2)', border: '1px solid var(--border2)',
                    borderRadius: 'var(--radius)', padding: '9px 14px', color: 'var(--text)',
                    fontSize: 14, outline: 'none', resize: 'vertical', minHeight: 90, lineHeight: 1.5,
                    fontFamily: 'var(--font-body)',
                  }}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6, fontFamily: 'var(--font-mono)' }}>Service offering</label>
                <Select value={form.service} onChange={e => set('service', e.target.value)} style={{ width: '100%' }}>
                  {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
                </Select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6, fontFamily: 'var(--font-mono)' }}>Tone</label>
                  <Select value={form.tone} onChange={e => set('tone', e.target.value)} style={{ width: '100%' }}>
                    <option value="direct">Direct & punchy</option>
                    <option value="friendly">Friendly & casual</option>
                    <option value="professional">Professional</option>
                    <option value="bold">Bold & confident</option>
                  </Select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6, fontFamily: 'var(--font-mono)' }}>Platform</label>
                  <Select value={form.platform} onChange={e => set('platform', e.target.value)} style={{ width: '100%' }}>
                    <option value="X DM">X / Twitter DM</option>
                    <option value="Discord DM">Discord DM</option>
                    <option value="Telegram DM">Telegram DM</option>
                    <option value="X reply">X public reply</option>
                  </Select>
                </div>
              </div>
              {error && <div style={{ fontSize: 13, color: 'var(--red)', padding: '8px 12px', background: 'var(--red-dim)', borderRadius: 'var(--radius)' }}>{error}</div>}
              <Btn onClick={generate} style={{ width: '100%', justifyContent: 'center' }}>
                {loading ? <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={15} />}
                {loading ? 'Writing your pitch...' : 'Generate pitch'}
              </Btn>
            </div>
          </Card>
        </div>

        <div>
          <SectionLabel>Your personalized pitch</SectionLabel>
          <Card style={{ minHeight: 300 }}>
            {!result && !loading && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240, gap: 12 }}>
                <Zap size={32} color="var(--text3)" />
                <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', lineHeight: 1.6 }}>Fill in the project details<br />and hit Generate</div>
              </div>
            )}
            {loading && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240, gap: 12 }}>
                <div style={{ width: 32, height: 32, border: '2px solid var(--accent-dim)', borderTop: '2px solid var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>Writing your pitch...</div>
              </div>
            )}
            {result && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 13, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>Ready to send</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={generate} style={{
                      display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
                      padding: '5px 12px', background: 'transparent', color: 'var(--text2)',
                      border: '1px solid var(--border2)', borderRadius: 'var(--radius)', cursor: 'pointer',
                    }}>
                      <RefreshCw size={12} /> Regenerate
                    </button>
                    <button onClick={copy} style={{
                      display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
                      padding: '5px 12px', background: copied ? 'var(--accent-dim)' : 'var(--surface2)',
                      color: copied ? 'var(--accent)' : 'var(--text2)',
                      border: '1px solid var(--border2)', borderRadius: 'var(--radius)', cursor: 'pointer',
                      fontFamily: 'var(--font-mono)',
                    }}>
                      {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
                <div style={{
                  background: 'var(--bg2)', borderRadius: 'var(--radius)',
                  padding: '16px 18px', fontSize: 14, lineHeight: 1.8,
                  color: 'var(--text)', whiteSpace: 'pre-wrap',
                  borderLeft: '2px solid var(--accent)',
                  fontFamily: 'var(--font-body)',
                }}>{result}</div>
              </div>
            )}
          </Card>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
